<template>
    <div class="animated fadeIn">
        <LoadingWidget v-if="initStatus != 1" :status="initStatus" @init="init" />
        <b-card v-else>
            <h5 class="mb-0"><i class="fa fa-fw fa-refresh mr-2" />Reset Inventory</h5>
            <hr />
            <b-row>
                <b-col md="3">
                    <b-form-group label="No. Faktur Barang">
                        <b-form-input v-model="filter.noFaktur" maxlength="50" @keydown.enter="getItems" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Status">
                        <b-form-select v-model="filter.status" :options="optFilterStatus" />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Office">
                        <multiselect v-model="filter.office"
                            label="name"
                            track-by="id"
                            :internal-search="true"
                            deselect-label=""
                            select-label=""
                            :options="optOffice"
                        />
                    </b-form-group>
                </b-col>
                <b-col md="3">
                    <b-form-group label="Aksi">
                        <b-button variant="primary" @click="getItems()">
                            <i class="fa fa-search mr-2" />Cari
                        </b-button>
                    </b-form-group>
                </b-col>
            </b-row>
            <b-table bordered hover show-empty no-local-sorting
                :busy="isBusy"
                :items="items"
                :fields="fields"
                @sort-changed="onSortChanged"
                responsive class="mb-3"
            >
                <template v-slot:empty>
                    Tidak ada data reset inventory.
                </template>
                <template v-slot:cell(noFaktur)="row">
                    {{ row.item.inventory.no_faktur_pgi }}
                    <div class="small text-muted">
                        <strong>{{ row.item.inventory.imei_sn }}</strong>
                    </div>
                    <div class="small text-muted">
                        {{ row.item.office_name }}
                    </div>
                </template>
                <template v-slot:cell(inventory)="row">
                    {{ row.item.inventory.item_type.name }} <span v-if="row.item.inventory.year != null">({{ row.item.inventory.year }})</span>
                    <div class="small text-muted">
                        {{ row.item.inventory.item_type.brand.kind.name }} - {{ row.item.inventory.item_type.brand.name }}
                    </div>
                </template>
                <template v-slot:cell(created)="row">
                    {{ $helper.parseDate(row.item.requested_at) }}, {{ $helper.parseHourMinute(row.item.requested_at) }}
                    <div class="small font-weight-bold">
                        {{ row.item.requested_by.fullname }}
                    </div>
                    <div class="small">
                        {{ row.item.note }}
                    </div>
                </template>
                <template v-slot:cell(proposal)="row">
                    <template v-if="row.item.proposed_data">
                        <div>{{ row.item.category || "-" }}</div>
                        <b-button size="sm" variant="outline-primary" class="mt-1" @click="showProposal(row.item)">
                            <i class="fa fa-eye mr-1" />Lihat Data Perubahan
                        </b-button>
                    </template>
                    <span v-else class="small text-muted">Tanpa data perubahan (diisi SM di temuan)</span>
                </template>
                <template v-slot:cell(status)="row">
                    <b-badge :variant="getStatusBadge(row.item.status)[0]">{{ getStatusBadge(row.item.status)[1] }}</b-badge>
                </template>
                <template v-slot:cell(action)="row">
                    <div v-if="row.item.status == 1">
                        <b-button size="sm" variant="success" v-b-tooltip.hover.top title="Terima" @click="acceptRequest(row.item)">
                            <i class="fa fa-fw fa-check" />
                        </b-button>
                        <b-button size="sm" class="ml-2" variant="danger" v-b-tooltip.hover.top title="Tolak" @click="rejectRequest(row.item)">
                            <i class="fa fa-fw fa-times" />
                        </b-button>
                    </div>
                </template>
            </b-table>
            <PageSizer :totalRows="totalRows" :page="currentPage" :options="options" @limit-change="onLimitChange" @page-change="onPageChange" />
        </b-card>

        <b-modal id="m-data-perubahan-reset" title="Data perubahan Reset Inventory" size="lg" hide-footer>
            <template v-if="activeItem.proposed_data">
                <div class="mb-2">
                    NF <strong>{{ activeItem.inventory.no_faktur_pgi }}</strong>
                    - Kategori: <strong>{{ activeItem.category || "-" }}</strong>
                </div>
                <table class="table table-sm table-bordered">
                    <tr v-for="field in compareFields" :key="field.key">
                        <td width="180" class="font-weight-bold">{{ field.label }}</td>
                        <td>{{ displayField(activeItem.proposed_data, field) }}</td>
                    </tr>
                </table>
                <div class="mb-2"><strong>Catatan:</strong> {{ activeItem.note || "-" }}</div>
                <div v-if="activeMedia.physical.length" class="mb-2">
                    <strong>Foto Fisik:</strong>
                    <div class="d-flex flex-wrap mt-1">
                        <a v-for="(m, index) in activeMedia.physical" :key="'p' + index" :href="m.url" target="_blank" @click="previewMedia($event, m.url)" class="mr-2 mb-2 text-center">
                            <MediaImage :src="m.url" class="media-thumbnail" :alt="m.category" />
                            <small class="d-block">{{ m.category }}</small>
                        </a>
                    </div>
                </div>
                <div class="d-flex flex-wrap">
                    <a
                        v-for="(m, index) in activeMedia.evidence"
                        :key="index"
                        :href="m.url"
                        target="_blank"
                        @click="previewMedia($event, m.url)"
                        class="mr-2 mb-2"
                    >
                        <video v-if="m.type === 'video'" :src="m.url" class="media-thumbnail" preload="metadata"></video>
                        <MediaImage v-else :src="m.url" class="media-thumbnail" alt="Media data perubahan" />
                    </a>
                </div>
            </template>
        </b-modal>
    </div>
</template>

<script>
import { COMPARE_FIELDS, displayField, getVarianceStatusBadge, splitMedia } from "@/shared/inventory-variance";
import MediaImage from "../temuan-inventory/MediaImage.vue";
import { previewMedia } from "@/shared/heic";

export default {
    components: { MediaImage },
    data() {
        return {

            initStatus: 0,
            isBusy: true,
            isSubmitting: false,
            show: 10,

            filter: {
                noFaktur: "",
                status: -1,
                office: null
            },

            options: [
                { value: 10, text: '10'},
                { value: 20, text: '20'},
                { value: 30, text: '30'},
                { value: 40, text: '40'},
                { value: 50, text: '50'},
            ],

            fields: [
                { key: "noFaktur", label: "No. Faktur / IMEI", sortable: true },
                { key: "inventory", label: "Barang", sortable: true },
                { key: "created", label: "Tgl. Request", sortable: true },
                { key: "proposal", label: "Data Perubahan" },
                { key: "status", label: "Status", sortable: true },
                { key: "action", label: "Aksi" },
            ],

            items: [],
            activeItem: {
                id: null,
                inventory: {}
            },
            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            sortBy: null,
            sortDesc: false,

            optFilterStatus: [
                { value: -1, text: "Semua" },
                { value: 1, text: "Pending" },
                { value: 2, text: "Diterima" },
                { value: 0, text: "Ditolak" }
            ],

            optOffice: [],

            compareFields: COMPARE_FIELDS,

        }
    },

    computed: {
        activeMedia() {
            return splitMedia(this.activeItem.media);
        }
    },

    methods: {
        previewMedia,

        displayField,
        getVarianceStatusBadge,

        showProposal(item) {
            this.activeItem = item
            this.$bvModal.show("m-data-perubahan-reset")
        },

        init() {

            this.initStatus = 0
            let getItems = this.getItems()

            let getOffices = this.$axios.get("master/offices").then(res => {
                this.optOffice = res.data.data
            })

            Promise.all([getItems, getOffices]).then(() => {
                this.initStatus = 1
            })
            .catch(_ => {
                this.initStatus = -1
            })

        },

        getItems(page = 1) {

            this.isBusy = true
            this.items = []

            if(isNaN(page)) {
                this.currentPage = 1
            }
            else {
                this.currentPage = page
            }

            // URL builder
            let param = this.$helper.getBasePagedQuery(this.currentPage, this.perPage)

            if(!this.$helper.isBlank(this.filter.noFaktur)) {
                param += "&filterNoFaktur=" + this.filter.noFaktur
            }

            if(this.filter.status != -1) {
                param += "&filterStatus=" + this.filter.status
            }

            if(this.filter.office != null) {
                param += "&filterOffice=" + this.filter.office.id
            }

            if(!this.$helper.isBlank(this.sortBy)) {
                param += "&sortBy=" + this.sortBy
                param += "&sortDesc=" + this.sortDesc
            }

            return this.$axios.get("inventory-reset" + param).then(res => {
                this.items = res.data.data.items
                this.totalRows = res.data.data.total
                sessionStorage.setItem("listInventoryResetFilter", JSON.stringify(this.filter))
            })
            .catch(error => {
                this.$helper.parseError(this, error)
            })
            .finally(() => {
                this.isBusy = false
            })

        },

        acceptRequest(item) {

            this.activeItem = item

            let isConfirmed = confirm(`Lakukan approval untuk request reset NF ${item.inventory.no_faktur_pgi}? Data barang akan direset menjadi PENDING dan temuan akan dibuat!`)
            if(!isConfirmed) return

            this.isSubmitting = true
            let payload = {
                id: this.activeItem.id,
                status: 2
            }

            this.$axios.post("inventory-reset/process", payload).then(res => {

                const varianceId = res.data.data ? res.data.data.variance_id : null
                this.$helper.toastSuccess(this, varianceId
                    ? `Request berhasil diapprove!, check list temuan`
                    : "Request berhasil diapprove!")
                this.getItems(this.currentPage)

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        rejectRequest(item) {

            this.activeItem = item

            let isConfirmed = confirm(`Tolak request reset NF ${item.inventory.no_faktur_pgi}? Data barang akan dikembalikan seperti sebelum request, mengikuti data terakhir!`)
            if(!isConfirmed) return

            this.isSubmitting = true

            let payload = {
                id: this.activeItem.id,
                status: 0
            }

            this.$axios.post("inventory-reset/process", payload).then(_ => {

                this.$helper.toastSuccess(this, "Request berhasil ditolak!")
                this.getItems(this.currentPage)

            })
            .catch(error => this.$helper.parseError(this, error))
            .finally(() => this.isSubmitting = false)

        },

        onLimitChange(newPerPage) {
            this.perPage = newPerPage
            this.getItems(this.currentPage)
        },

        onPageChange(newPage) {
            this.currentPage = newPage
            this.getItems(this.currentPage)
        },

        onSortChanged(ctx) {
            this.sortBy = ctx.sortBy
            this.sortDesc = ctx.sortDesc
            this.getItems()
        },

        getStatusBadge(status) {
            switch(status) {
                case 1: return ["warning", "PENDING"]
                case 2: return ["success", "DITERIMA"]
                case 0: return ["danger", "DITOLAK"]
                default: return ["light", "-"]
            }
        }

    },

    created() {
        if(!this.$helper.isBlank(sessionStorage.getItem("listInventoryResetFilter"))){
            this.filter = JSON.parse(sessionStorage.getItem("listInventoryResetFilter"))
        }
        this.init()
    }
}
</script>

<style scoped>
.media-thumbnail {
    width: 80px;
    height: 80px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid #e9ecef;
}
</style>
